import i18n from "@plugins/i18n.ts";
import PageDirtyMixin from "@modules/app/mixin/page.dirty.mixin";

export default {
  mixins: [PageDirtyMixin],
  props: {
    visible: { type: Boolean, default: true },
    theme: { type: String, default: "" },
    label: { type: String, default: "" },
    value: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: "" },
    helperText: { type: String, default: "" },
    characterLimit: { type: Number, default: 50 }, //if characterLimit is set to 0 then no validation is done
    useSecondaryCharacterLimit: { type: Boolean, default: false },
    secondaryCharacterLimit: { type: Number, default: 500 }, // used in keywords for character limit of each tag
    mandatory: { type: Boolean, default: false },
    errorMessage: { type: String, default: "" },
    doValidate: { type: Boolean, default: false },
    customValidation: { type: Boolean, default: false },
    textId: { type: String },
    enableWordCount: { type: Boolean, default: false },
  },
  emits: ["change", "update:value", "input", "validated"],
  data() {
    return {
      inputValue: "",
      invalidMessage: "",
    };
  },
  computed: {
    dirtyId() {
      return this.textId;
    },
    inputCount() {
      return this.inputValue?.replace(/\n/g, "").length ?? 0; // exclude newlines
    },
  },
  watch: {
    errorMessage(error) {
      this.invalidMessage = error;
    },
    value(newValue) {
      this.inputValue = newValue;
    },
    doValidate() {
      if (this.doValidate) {
        this.validate();
      }
    },
  },
  methods: {
    handlePaste(event) {
      const pastedText = event.clipboardData.getData("Text");
      const totalLength = this.inputValue.length + pastedText.length;

      if (this.characterLimit !== 0) {
        if (totalLength > this.characterLimit) {
          this.displayExceedCharacterLimitMessage(this.characterLimit);
        } else if (this.useSecondaryCharacterLimit && totalLength > this.secondaryCharacterLimit) {
          this.displayExceedCharacterLimitMessage(this.secondaryCharacterLimit);
        }
      }
    },
    handleChange() {
      if (this.validateEmpty()) {
        this.$emit("update:value", this.inputValue);
        this.$emit("change", this.inputValue);
      }
    },
    handleInput(inputValue) {
      if (this.dirtyId) {
        this.setDirty(this.dirtyId);
      }
      this.inputValue = inputValue;

      if (this.characterLimit !== 0 && this.validateMaxLimit()) {
        this.invalidMessage = "";
      }
      this.$emit("input", this.inputValue);
    },
    validate() {
      this.invalidMessage = "";
      let isValid = true;
      isValid = isValid && this.validateEmpty();
      isValid = isValid && (this.characterLimit <= 0 || this.validateMaxLimit()); //if characterLimit is set to 0 then no validation is done
      this.$emit("validated", isValid);
      return isValid;
    },
    validateEmpty() {
      const inputValue = this.inputValue;
      if (this.mandatory && inputValue.trim() === "") {
        this.invalidMessage = i18n.t("field_empty_error_message");
        return false;
      }
      return true;
    },
    validateMaxLimit() {
      if (this.inputCount > this.characterLimit) {
        this.displayExceedCharacterLimitMessage(this.characterLimit);
        return false;
      } else if (
        this.useSecondaryCharacterLimit &&
        this.inputCount > this.secondaryCharacterLimit
      ) {
        this.displayExceedCharacterLimitMessage(this.secondaryCharacterLimit);
        return false;
      }
      return true;
    },
    displayExceedCharacterLimitMessage(characterLimit) {
      if (this.customValidation) {
        this.invalidMessage = this.errorMessage;
      } else {
        this.invalidMessage = i18n.t("character_limit_invalid_message", {
          num: characterLimit,
        });
      }
    },
  },
  mounted() {
    this.inputValue = this.value;
  },
  beforeUnmount() {
    if (this.dirtyId) {
      this.removeDirty(this.dirtyId);
    }
  },
};
